import { PerimeterStatsService } from './perimeter-stats.service';
import { InvestmentScenariiService } from './investment-scenarii.service';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { concatMap, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { KPI } from '../api';
import { APIService } from './../api/api.service';
import { ChartsService } from './charts.service';
import {
  YEARLY_SAVINGS_FILTER_STORAGE_KEY,
  KWH_SAVINGS_FILTER_STORAGE_KEY,
  CEE_GAINS_FILTER_STORAGE_KEY,
  COUNT_LINKED_INITIATIVES_FILTER_STORAGE_KEY,
  MAX_PAYBACK_YEARS_STORAGE_KEY,
} from './constants';
import { PerimetersService } from './perimeters.service';
import { idMapToArray, ScopeService } from './scope.service';
import { UsersService } from './users.service';
import { environment } from '../../../environments/environment';
import { UtilsService } from './utils.service';
import { StyleService } from './style.service';

export interface ObservableMap<T> {
  [key: string]: Observable<T>;
}

@Injectable({
  providedIn: 'root',
})
export class InitiativesService {

  public newInitiative$ = new BehaviorSubject<KPI.Initiative>(null);

  constructor(
    private apiService: APIService,
    private usersService: UsersService,
    private perimetersService: PerimetersService,
    private chartsService: ChartsService,
    private api: APIService,
    private translate: TranslateService,
    private scopeService: ScopeService,
    private investmentScenariiService: InvestmentScenariiService,
    private utils: UtilsService,
    private styleService: StyleService,
    private perimeterStatsService: PerimeterStatsService,
  ) { }

  public paybackMonths$: BehaviorSubject<number> = new BehaviorSubject<number>(36);
  public refresh$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private actionTagsRefresh$ = new BehaviorSubject<void>(null);
  private _actionTags$: Observable<KPI.InitiativeTag[]>;

  private confidenceMap = {
    [KPI.Confidence.LOW]: this.translate.instant('asset.confidence_levels.low'),
    [KPI.Confidence.MEDIUM]: this.translate.instant('asset.confidence_levels.medium'),
    [KPI.Confidence.HIGH]: this.translate.instant('asset.confidence_levels.high'),
  };

  public getInitiative(id: number): Observable<KPI.MappedInitiative> {
    return this.apiService.get_initiative(id).pipe(
      //@ts-ignore
      concatMap((initiative) => this.mapInitiatives([initiative])),
      map(([initiative]) => initiative),
    );
  }

  public getInitiatives(
    perimeters?: KPI.Perimeter[],
  ): Observable<KPI.MappedInitiative[]> {
    return this.apiService.get_initiatives({
      ...(
        perimeters?.length
        ? {
          perimeters: perimeters.map(({id}) => id).join(','),
        }
        : {}
      ),
    }).pipe(
      concatMap((initiatives) => this.mapInitiatives(initiatives)),
    );
  }

  private mapInitiatives(initiatives: KPI.Initiative[]): Observable<KPI.MappedInitiative[]> {
    return combineLatest([
      this.initiativeStatuses$,
      this.initiativeTypes$,
      this.initiativeOwners$,
      this.usersService.allUsers$,
      this.perimetersService.allPerimeters$,
      this.scopeService.investmentTypes$.pipe(first(), idMapToArray()),
      this.scopeService.investmentStatuses$.pipe(first(), idMapToArray()),
      this.investmentScenariiService.scenarii$,
      this.scopeService.confidencesOnFeasibility$,
    ]).pipe(
      first(),
      map(([statuses, types, owners, users,
        perimeters, investmentTypes, investmentStatuses,
        scenarii, confidencesOnFeasibility,
      ]) => {
        return initiatives.map((initiative) => {
          let perimeter: KPI.Perimeter;
          let site: KPI.Perimeter;
          perimeters.map((p) => {
            if (p.id === initiative.site) {
              site = p;
            }
            if (p.sub_perimeters && p.sub_perimeters.length && !perimeter) {
              perimeter = p.sub_perimeters.find((sp) => sp.id === initiative.perimeter);
            }
          });
          const assets = initiative.assets ? initiative.assets : [];
          return {
            ...initiative,
            status: statuses.find((status) => status.id === initiative.status),
            initiative_type: types.find((type) => type.id === initiative.initiative_type),
            initiative_type_parent: types.find(
              (type) =>
                type.id === types.find((type) => type.id === initiative.initiative_type).parent
            ),
            owner: owners.find((owner) => owner.id === initiative.owner),
            created_by: users.find((user) => user.get_user_id === initiative.created_by),
            perimeter,
            site,
            confidence_on_theoretical_savings_name: this.confidenceMap[initiative.confidence_on_theoretical_savings],
            confidence_on_feasibility_name: confidencesOnFeasibility[initiative.confidence_on_feasibility]?.name,
            assets,
            investment: initiative.investment ? {
              ...initiative.investment,
              investment_type_object: investmentTypes.find((type) => type.id === initiative.investment.investment_type),
              status_object: investmentStatuses.find((status) => status.id === initiative.investment.status),
            } : null,
            instances: initiative.instances
              ? initiative.instances.map((instance) => ({
                ...instance,
                status_object: statuses.find((status) => status.id === instance.status),
                scenario_object: scenarii.find((scenario) => scenario.id === instance.scenario),
              }))
              : []
          };
        });
      }),
    );
  }

  private initiativeStatuses: Observable<KPI.InitiativeStatus[]>;
  public get initiativeStatuses$(): Observable<KPI.InitiativeStatus[]> {
    if (!this.initiativeStatuses) {
      this.initiativeStatuses = this.apiService.get_initiative_statuses().pipe(shareReplay(1));
    }
    return this.initiativeStatuses;
  }

  private initiativeTypes: Observable<KPI.InitiativeType[]>;
  public get initiativeTypes$(): Observable<KPI.InitiativeType[]> {
    if (!this.initiativeTypes) {
      this.initiativeTypes = this.apiService.get_initiative_types().pipe(shareReplay(1));
    }
    return this.initiativeTypes;
  }

  private initiativeOwners: Observable<KPI.InitiativeOwner[]>;
  public get initiativeOwners$(): Observable<KPI.InitiativeOwner[]> {
    if (!this.initiativeOwners) {
      this.initiativeOwners = this.apiService.get_initiative_owners().pipe(shareReplay(1));
    }
    return this.initiativeOwners;
  }

  private initiativeEnergyTypes: Observable<KPI.InitiativeEnergyType[]>;
  public get initiativeEnergyTypes$(): Observable<KPI.InitiativeEnergyType[]> {
    if (!this.initiativeEnergyTypes) {
      this.initiativeEnergyTypes = this.apiService.get_initiative_energy_types().pipe(
        map((energyTypes) =>
          energyTypes.sort((a, b) => a.order - b.order)
        ),
        tap((energyTypes) =>
          energyTypes
            .filter((energyType) => energyType.color)
            .forEach((energyType) => {
              this.styleService.setStyles(
                `.energy-type-${energyType.id} .ag-sticky-label, .energy-type-${energyType.id} .ag-header-cell-label`,
                {
                  backgroundColor: StyleService.hexToRGB(energyType.color, 0.75),
                  alignSelf: 'auto',
                  padding: '4px 10px 5px',
                  borderRadius: '5px',
                  justifyContent: 'center',
                  marginRight: '5px',
                  width: '100%',
                },
              );
            }),
        ),
        shareReplay(1),
      );
    }
    return this.initiativeEnergyTypes;
  }


  private relevantEnergyTypes: Observable<KPI.InitiativeEnergyType[]>;
  public get relevantEnergyTypes$(): Observable<KPI.InitiativeEnergyType[]> {
    if (!this.relevantEnergyTypes) {
      this.relevantEnergyTypes = combineLatest([
        this.perimetersService.perimeters$.pipe(
          switchMap((perimeters) => this.perimeterStatsService.perimeterEnergyIds(perimeters.map((p) => p.id))),
        ),
        this.initiativeEnergyTypes$,
      ]).pipe(
        map(([relevantEnergyIds, energyTypes]) => energyTypes.filter((et) => relevantEnergyIds.includes(et.id))),
        shareReplay(1),
      );
    }
    return this.relevantEnergyTypes;
  }

  private emissionCategories: Observable<KPI.EmissionCategory[]>;
  public get emissionCategories$(): Observable<KPI.EmissionCategory[]> {
    if (!this.emissionCategories) {
      this.emissionCategories = this.apiService.get_emission_categories().pipe(shareReplay(1));
    }
    return this.emissionCategories;
  }

  public getGHGEmissionsCard(options): Observable<KPI.GHGEmissionsCard> {
    return this.chartsService.getGHGEmissionsChart(options).pipe(
      map((data) => {
        const startYear = options.start_year;
        const endYear = options.end_year;
        const initiatives = data.initiatives;
        if (startYear && endYear && initiatives) {
          const yearsRange = R.range(startYear, endYear + 1);
          const totalEmissions: number = Object.keys(initiatives).reduce((acc, key) => {
            return acc += yearsRange.reduce((acc2, year) => {
              if (initiatives[key][year]) {
                acc2 += initiatives[key][year];
              }
              return acc2;
            }, 0);
          }, 0);
          return {
            total: totalEmissions,
          };
        } else {
          return {
            total: null,
          };
        }
      }),
    );
  }

  public createInitiative(
    initiative: Partial<KPI.Initiative>,
  ): Observable<KPI.Initiative> {
    return this.api.initiatives_initiatives_post(initiative).pipe(
      tap((newInitiative) => {
        this.chartsService.energyHasChanged();
        this.newInitiative$.next(newInitiative);
      }),
    );
  }

  /**
   * Updates an initiative in the data base
   *
   * @param id
   * @param updatedInitiative
   */
  public updateInitiative(
    id: number,
    updatedInitiative: Partial<KPI.Initiative>,
  ): Observable<KPI.Initiative> {
    const numericFields = ['cee_bonus', 'yearly_cost_savings'];
    const sanitizedInitiative = { ...updatedInitiative };
    numericFields.forEach(field => {
      if (sanitizedInitiative[field] === null) {
        sanitizedInitiative[field] = 0;
      }
    });
    
    return this.api.initiatives_initiatives_patch(id, sanitizedInitiative);
  }

  public deleteInitiative(id: number): Observable<void> {
    return this.api.initiatives_initiatives_delete(id).pipe(
      tap(() => this.chartsService.energyHasChanged()),
    );
  }

  private _yearlySavingsFilter$ = new BehaviorSubject<boolean>(null);
  private cachedYearlySavingsFilterLoaded: boolean = false;
  public get yearlySavingsFilter$(): Observable<boolean> {
    if (!this.cachedYearlySavingsFilterLoaded) {
      const cachedFilter = localStorage.getItem(
        YEARLY_SAVINGS_FILTER_STORAGE_KEY,
      );
      if (cachedFilter) {
        this._yearlySavingsFilter$.next(JSON.parse(cachedFilter));
      } else {
        this._yearlySavingsFilter$.next(true);
      }
      this.cachedYearlySavingsFilterLoaded = true;
    }
    return this._yearlySavingsFilter$.pipe(
      filter((res) => res !== null),
    );
  }

  public setYearlySavingsFilter(filterValue: boolean): void {
    if (filterValue !== this._yearlySavingsFilter$.value) {
      this._yearlySavingsFilter$.next(filterValue);
      localStorage.setItem(
        YEARLY_SAVINGS_FILTER_STORAGE_KEY,
        JSON.stringify(filterValue),
      );
    }
  }

  private _kwhSavingsFilter$ = new BehaviorSubject<boolean>(null);
  private cachedKwhSavingsFilterLoaded: boolean = false;
  public get kwhSavingsFilter$(): Observable<boolean> {
    if (!this.cachedKwhSavingsFilterLoaded) {
      const cachedFilter = localStorage.getItem(
        KWH_SAVINGS_FILTER_STORAGE_KEY,
      );
      if (cachedFilter) {
        this._kwhSavingsFilter$.next(JSON.parse(cachedFilter));
      } else {
        this._kwhSavingsFilter$.next(true);
      }
      this.cachedKwhSavingsFilterLoaded = true;
    }
    return this._kwhSavingsFilter$.pipe(
      filter((res) => res !== null),
    );
  }

  public setKwhSavingsFilter(filterValue: boolean): void {
    if (filterValue !== this._kwhSavingsFilter$.value) {
      this._kwhSavingsFilter$.next(filterValue);
      localStorage.setItem(
        KWH_SAVINGS_FILTER_STORAGE_KEY,
        JSON.stringify(filterValue),
      );
    }
  }

  private _CEEGainsFilter$ = new BehaviorSubject<boolean>(null);
  private cachedCEEGainsFilterLoaded: boolean = false;
  public get CEEGainsFilter$(): Observable<boolean> {
    if (!this.cachedCEEGainsFilterLoaded) {
      const cachedFilter = localStorage.getItem(
        CEE_GAINS_FILTER_STORAGE_KEY,
      );
      if (cachedFilter) {
        this._CEEGainsFilter$.next(JSON.parse(cachedFilter));
      } else {
        this._CEEGainsFilter$.next(true);
      }
      this.cachedCEEGainsFilterLoaded = true;
    }
    return this._CEEGainsFilter$.pipe(
      filter((res) => res !== null),
    );
  }

  public setCEEGainsFilter(filterValue: boolean): void {
    if (filterValue !== this._CEEGainsFilter$.value) {
      this._CEEGainsFilter$.next(filterValue);
      localStorage.setItem(
        CEE_GAINS_FILTER_STORAGE_KEY,
        JSON.stringify(filterValue),
      );
    }
  }

  private _countLinkedInitiativesFilter$ = new BehaviorSubject<boolean>(null);
  private cachedCountLinkedInitiativesFilter: boolean = false;
  public get countLinkedInitiativesFilter$(): Observable<boolean> {
    if (!this.cachedCountLinkedInitiativesFilter) {
      const cachedFilter = localStorage.getItem(
        COUNT_LINKED_INITIATIVES_FILTER_STORAGE_KEY,
      );
      if (cachedFilter) {
        this._countLinkedInitiativesFilter$.next(JSON.parse(cachedFilter));
      } else {
        this._countLinkedInitiativesFilter$.next(true);
      }
      this.cachedCountLinkedInitiativesFilter = true;
    }
    return this._countLinkedInitiativesFilter$.pipe(
      filter((res) => res !== null),
    );
  }

  public setCountLinkedInitiativesFilter(filterValue: boolean): void {
    if (filterValue !== this._countLinkedInitiativesFilter$.value) {
      this._countLinkedInitiativesFilter$.next(filterValue);
      localStorage.setItem(
        COUNT_LINKED_INITIATIVES_FILTER_STORAGE_KEY,
        JSON.stringify(filterValue),
      );
    }
  }

  private energyReportSections: Observable<KPI.EnergyReportSection[]>
  public get energyReportSections$(): Observable<KPI.EnergyReportSection[]> {
    if (!this.energyReportSections) {
      this.energyReportSections = this.apiService.get_energy_report_sections().pipe(shareReplay(1));
    }
    return this.energyReportSections;
  }

  public openAuditInitiativeEditor(configuration: { initiativeId: number, siteId: number }) {
    const { initiativeId, siteId } = configuration
    const popupUrl = `${environment.auditAppURL}/initiatives/${siteId}/initiative-detail/${initiativeId}`
    this.utils.openPopupWindow(popupUrl)
  }

  public get actionTags$(): Observable<KPI.InitiativeTag[]> {
    if (!this._actionTags$) {
      this._actionTags$ = this.actionTagsRefresh$.pipe(
        switchMap(() => this.api.get_initiative_tags()),
        shareReplay(1),
      );
    }
    return this._actionTags$;
  }

  public get gainOrigins$(): Observable<KPI.GainOrigin[]> {
    return this.api.get_gain_origins().pipe(
      shareReplay(1),
    );
  }

  public get gainPrecisions$(): Observable<KPI.GainPrecision[]> {
    return this.api.get_gain_precisions().pipe(
      shareReplay(1),
    );
  }

  private readonly defaultPaybackYears = 10;
  private _maxPaybackYears$ = new BehaviorSubject<number>(this.defaultPaybackYears);
  private cachedMaxPaybackYearsLoaded: boolean = false;

  public get maxPaybackYears$(): Observable<number> {
    if (!this.cachedMaxPaybackYearsLoaded) {
      const cachedMaxPaybackYears = localStorage.getItem(MAX_PAYBACK_YEARS_STORAGE_KEY);
      this._maxPaybackYears$.next(
        cachedMaxPaybackYears ? JSON.parse(cachedMaxPaybackYears) : this.defaultPaybackYears
      )
      this.cachedMaxPaybackYearsLoaded = true;
    }
    return this._maxPaybackYears$;
  }

  public setMaxPaybackYears(value: number): void {
    if (value !== this._maxPaybackYears$.value) {
      this._maxPaybackYears$.next(value);
      localStorage.setItem(MAX_PAYBACK_YEARS_STORAGE_KEY, JSON.stringify(value));
    }
  }

}
